import { useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';

import {
  glacierPolicyApiGetPolicyQueryKey,
  glacierPolicyApiListUnderwriterNotesOptions,
  glacierPolicyApiSetPolicyRenewalFlagMutation,
} from '@/lib/heyapi/@tanstack/react-query.gen';
import { RenewalFlagEnum } from '@/lib/heyapi/types.gen';

import type { Message } from '../../StandardMessage';
import { FLAGGED_FOR_MANUAL_REVIEW_NOTE } from './helpers';

export const FlagForManualRenew = ({
  accountId,
  isOpen,
  onOpenChange,
  setMessage,
}: {
  accountId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  setMessage: (message: Message | undefined) => void;
}) => {
  const [note, setNote] = useState('');
  const queryClient = useQueryClient();

  const { queryKey } = glacierPolicyApiListUnderwriterNotesOptions({
    path: {
      account_id: accountId,
    },
  });

  const policyQK = glacierPolicyApiGetPolicyQueryKey({
    path: {
      account_id: accountId,
    },
  });

  const { mutate: flagForManualRenew, isPending } = useMutation({
    ...glacierPolicyApiSetPolicyRenewalFlagMutation(),
    onMutate: () => {
      setMessage({
        content: 'Flagging policy for manual review...',
        level: 'INFO',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: policyQK });
      queryClient.invalidateQueries({ queryKey });
      setNote('');
      onOpenChange(false);
      toast({
        title: 'Policy Flagged for Manual Review',
        description: <p>Policy flagged for manual review successfully</p>,
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: (
          <p>
            {(error as { detail?: string })?.detail ||
              'An error occurred while flagging the policy for manual review'}
          </p>
        ),
      });
    },
    onSettled: () => {
      setMessage(undefined);
    },
  });

  const handleFlagForManualReview = () => {
    flagForManualRenew({
      body: {
        reasonForNonRenewal: null,
        note: `${FLAGGED_FOR_MANUAL_REVIEW_NOTE} ${note.trim()}`,
      },
      path: {
        account_id: accountId,
        flag: RenewalFlagEnum.FLAG_FOR_MANUAL_REVIEW,
      },
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Flag for Manual Review</DialogTitle>
          <DialogDescription>
            Marking this account as Manual Review will keep the system from
            automatically creating a renewal for this account. Do you wish to
            continue?
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="manual-review-note">Reason for Manual Review</Label>
          <Textarea
            id="manual-review-note"
            placeholder="Reason for manual review..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={isPending}
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            disabled={isPending || !note.trim()}
            loading={isPending}
            onClick={handleFlagForManualReview}
          >
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
